import { format as dateFnsFormat, getUnixTime, Locale } from 'date-fns';
import { enUS, enGB, sv, fi, de, nl, srLatn } from 'date-fns/locale';

import { ISO_FORMAT } from '../../constants/time';
import i18n from '../../i18n';
import type { SupportedLanguageCodes } from '../../i18n';

const locales: Record<SupportedLanguageCodes | 'en', Locale> = {
  'en-US': enUS,
  'en-GB': enGB,
  en: enUS,
  sv,
  fi,
  de,
  nl,
  sr: srLatn,
};

type LocaleKey = keyof typeof locales;

const isLocaleKey = (lang: string): lang is LocaleKey => lang in locales;

const resolveLocale = (lang: string): Locale | undefined => {
  if (isLocaleKey(lang)) {
    return locales[lang];
  }

  const langCode = lang.split(/[-_]/)[0];
  if (isLocaleKey(langCode)) {
    return locales[langCode];
  }

  return undefined;
};

const getAppLocale = (): Locale => {
  const appLocale = resolveLocale(i18n.language);
  if (appLocale) return appLocale;

  if (typeof window === 'undefined') return enGB;

  return resolveLocale(navigator.language) || enGB;
};

export const newDate = (date?: string) => (date ? new Date(date) : new Date());

export const toISOFormat = (date: Date) => dateFnsFormat(date, ISO_FORMAT);

export const format = (
  date: Date | string,
  formatString: string,
  locale?: Locale | string,
) => {
  let selectedLocale: Locale;

  if (locale) {
    if (typeof locale === 'string') {
      selectedLocale = resolveLocale(locale) || getAppLocale();
    } else {
      selectedLocale = locale;
    }
  } else {
    selectedLocale = getAppLocale();
  }

  const dateObj = date instanceof Date ? date : new Date(date);
  return dateFnsFormat(dateObj, formatString, { locale: selectedLocale });
};

export const formatDate = (date: Date | string, locale?: Locale | string) =>
  format(date, 'P', locale);

export const formatDateTime = (date: Date | string, locale?: Locale | string) =>
  format(date, 'Pp', locale);

export const formatDateRange = (
  dateFrom: Date | string,
  dateTo: Date | string,
  locale?: Locale | string,
) => `${formatDate(dateFrom, locale)} – ${formatDate(dateTo, locale)}`;

export const toUnixTime = (date: Date) => getUnixTime(date);
